// Angular
import { Injectable } from '@angular/core';

// Ionic library
import { Platform } from '@ionic/angular';

// Third party library
// eslint-disable-next-line @nx/enforce-module-boundaries
import { Geolocation } from '@capacitor/geolocation';

@Injectable()
export class GeoLocationService {

  constructor(private platform: Platform) { }

  /**
   * Fetch current coordinates (high accuracy)
   * @returns {Promise<{latitude:number, longitude:number} | {status:boolean, msg:string, showDeviceSetting:boolean}>} Coords or error info
   */
  async getLocation():
    Promise<{ latitude: number; longitude: number } |
      {status: boolean, msg: string, showDeviceSetting: boolean}> {
    // Promise<{ latitude: number; longitude: number } | false> {
    try {
      const result = await Geolocation.getCurrentPosition({
        enableHighAccuracy: true,  // Ensures fine location is used
        // timeout: 10000, // Timeout after 10 seconds
        // maximumAge: 0 // Do not use cached location, always get fresh location
      });

      // threshold
      // if (result.coords.accuracy > 50) {
      //   return {
      //     msg: 'Location accuracy is too low. Please enable precise location.',
      //     status: false,
      //     showDeviceSetting: true
      //   };
      // }

      return {
        latitude: result.coords.latitude,
        longitude: result.coords.longitude
      };
    } catch (err) {
      // console.error('Error getting location.', err);
      // return false;
      return {
        msg: 'Error getting location. Reach out support for help.',
        status: false,
        showDeviceSetting: false
      };
    }
  }

  /**
   * Ensure permission is granted, then return coords (native or web)
   * @param {boolean} skipPermissionRequest True to skip the prompt (interval polling)
   * @returns {Promise<{latitude:number, longitude:number} | {status:boolean, msg:string, showDeviceSetting:boolean}>} Coords or error info
   */
  async askPermissionGeoLocation(skipPermissionRequest = false):
    Promise<{ latitude: number; longitude: number } |
      {status: boolean, msg: string, showDeviceSetting: boolean}> {
    // Promise<{ latitude: number; longitude: number } | false> {
    if (this.platform.is('hybrid')) {
      return await this.checkPermissionGeoLocationNative(skipPermissionRequest);
    }
    else {
      return await this.checkPermissionGeoLocationWeb(skipPermissionRequest);
    }

    // browser old
    // if (!navigator.geolocation) return;
    // navigator.geolocation.getCurrentPosition( (geo) => {
    //   this.clientGeo.latitude = geo.coords.latitude;
    //   this.clientGeo.longitude = geo.coords.longitude;
    // });
  }

  /**
   * Native (Capacitor) permission check + coordinate fetch
   * @param {boolean} skipPermissionRequest True to skip the permission prompt
   * @returns {Promise<{latitude:number, longitude:number} | {status:boolean, msg:string, showDeviceSetting:boolean}>} Coords or error info
   */
  private async checkPermissionGeoLocationNative(skipPermissionRequest: boolean):
    Promise<{ latitude: number; longitude: number } |
      {status: boolean, msg: string, showDeviceSetting: boolean}> {
    // Promise<{ latitude: number; longitude: number } | false> {
    try {
      const permissionStatus = await Geolocation.checkPermissions();

      if (
        permissionStatus.location === 'granted' ||
        permissionStatus.coarseLocation === 'granted'
      ) {
        return this.getLocation();
      }
      else {
        if (skipPermissionRequest) {
          return {
            msg: '',
            status: false,
            showDeviceSetting: false
          };
        }

        const permissionResult = await Geolocation.requestPermissions({
          permissions: ['location','coarseLocation']
        });

        if (
          permissionResult.location === 'granted' ||
          permissionResult.coarseLocation === 'granted'
        ) {
          return this.getLocation();
        }
        else if (
          permissionResult.location === 'prompt' ||
          permissionResult.coarseLocation === 'prompt' ||
          permissionStatus.location === 'prompt' ||
          permissionStatus.coarseLocation === 'prompt'
        ) {
          return {
            msg: '',
            status: false,
            showDeviceSetting: false
          };
        }
        else if (
          permissionResult.location === 'prompt-with-rationale' ||
          permissionResult.coarseLocation === 'prompt-with-rationale' ||
          permissionStatus.location === 'prompt-with-rationale' ||
          permissionStatus.coarseLocation === 'prompt-with-rationale'
        ) {
          return {
            msg: '',
            status: false,
            showDeviceSetting: false
          };
        }
        else if (
          permissionResult.location === 'denied' ||
          permissionResult.coarseLocation === 'denied'
        ) {
          // console.log('Geolocation permission denied.');
          // return false;
          return {
            msg: 'Error on granting permission. Open device settings?',
            status: false,
            showDeviceSetting: true
          };
        }
        else {
          return {
            msg: '',
            status: false,
            showDeviceSetting: false
          };
        }
      }
    } catch (err) {
      // console.error('Error checking geolocation permission.', err);
      // return false;
      return {
        msg: 'Error on granting permission. Open device settings?',
        status: false,
        showDeviceSetting: true
      };
    }
  }

  /**
   * Web (browser) permission check + coordinate fetch
   * @param {boolean} skipPermissionRequest True to skip the permission prompt
   * @returns {Promise<{latitude:number, longitude:number} | {status:boolean, msg:string, showDeviceSetting:boolean}>} Coords or error info
   */
  private async checkPermissionGeoLocationWeb(skipPermissionRequest: boolean):
    Promise<{ latitude: number; longitude: number } |
      {status: boolean, msg: string, showDeviceSetting: boolean}> {
    // Promise<{ latitude: number; longitude: number } | false> {
    try {
      const permissionStatus = await Geolocation.checkPermissions();

      if (permissionStatus.location === 'granted' || permissionStatus.location === 'prompt') {
        return this.getLocation();
      }
      // console.log('Geolocation permission not granted.');
      // return false;
      return {
        msg: 'Web: Error on granting permission. Reach out support for help.',
        status: false,
        showDeviceSetting: false
      };
    } catch (err) {
      // console.error('Error checking permission.', err);
      // return false;
      return {
        msg: 'Web: Error on granting permission. Reach out support for help.',
        status: false,
        showDeviceSetting: false
      };
    }
  }
}
